import "./GpsTrackerFormModal.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { GpsTrackerResponse } from "../../../shared/response/hardware/GpsTrackerResponse";
import { GpsTrackerPayload } from "../../../shared/services/api/payload/hardware/GpsTrackerPayload";
import { GpsTrackerSaveService } from "../../services/hardware/GpsTrackerSaveService";

/**
 * Modal crear/editar GPS Tracker. El frontend NO maneja códigos (los genera el backend):
 * solo campos de testeo — fechaTesteo (opcional), pasaTest (toggle), notaTest (si no pasa).
 * Al crear, el backend asigna codigoInterno y clienteId=null. El 400 se muestra vía el base.
 */
export class GpsTrackerFormModal extends BaseSmallModalFormComponent<
  GpsTrackerResponse,
  GpsTrackerPayload,
  GpsTrackerSaveService
> {
  private _fecha!: HTMLInputElement;
  private _pasa!: HTMLInputElement;
  private _nota!: HTMLTextAreaElement;

  constructor(
    private readonly _onSavedCb: (item: GpsTrackerResponse) => void,
    private readonly _item?: GpsTrackerResponse,
  ) {
    super(new GpsTrackerSaveService());
  }

  protected get title(): string { return this._item ? "Editar testeo" : "Registrar GPS Tracker"; }
  protected get saveText(): string { return "Guardar"; }
  protected get saveInitiallyDisabled(): boolean { return false; }

  protected renderBody(): string {
    const fechaVal = this._item?.fechaTesteo
      ? new Date(this._item.fechaTesteo).toISOString().slice(0, 10)
      : "";
    const pasa = this._item?.pasaTest ?? false;
    const nota = this._item?.notaTest ?? "";

    const codInfo = this._item
      ? `<div class="gt-form-code">Código interno · ${this._item.codigoInterno}</div>`
      : `<div class="gt-form-note">El código interno se asigna automáticamente al registrar. El dispositivo queda en oficina.</div>`;

    return `
      <div class="cl-form-fields">
        ${codInfo}

        <div class="cl-form-group">
          <label class="cl-form-label" for="gt-fecha">Fecha de testeo</label>
          <input id="gt-fecha" class="sx-input" type="date" value="${fechaVal}" />
          <span class="gt-form-note">Vacío = dispositivo sin testear.</span>
        </div>

        <label class="gt-check">
          <input id="gt-pasa" type="checkbox" ${pasa ? "checked" : ""} />
          <span>Pasa el test</span>
        </label>

        <div class="cl-form-group gt-nota-group" ${pasa ? "hidden" : ""}>
          <label class="cl-form-label" for="gt-nota">Nota / motivo de fallo</label>
          <textarea id="gt-nota" class="sx-input" rows="2" style="resize:vertical">${this._esc(nota)}</textarea>
        </div>
      </div>
    `;
  }

  protected override onAfterRender(el: HTMLDivElement): void {
    this._fecha = el.querySelector<HTMLInputElement>("#gt-fecha")!;
    this._pasa  = el.querySelector<HTMLInputElement>("#gt-pasa")!;
    this._nota  = el.querySelector<HTMLTextAreaElement>("#gt-nota")!;

    const notaGroup = el.querySelector<HTMLElement>(".gt-nota-group")!;
    this._pasa.addEventListener("change", () => {
      notaGroup.toggleAttribute("hidden", this._pasa.checked);
    });
  }

  protected buildPayload(): GpsTrackerPayload {
    const pasa = this._pasa.checked;
    const fecha = this._fecha.value;
    return {
      id: this._item?.id ?? null,
      fechaTesteo: fecha ? new Date(fecha).toISOString() : null,
      pasaTest: pasa,
      // Si pasa el test, el backend limpia la nota igual; enviamos null por coherencia.
      notaTest: pasa ? null : (this._nota.value.trim() || null),
    };
  }

  protected override onSaved(item: GpsTrackerResponse): void {
    this._onSavedCb(item);
    this.dispose();
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
